import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePriestAuth } from '../../context/PriestAuthContext';

const newItem = () => ({ description: '', quantity: 1, rate: 0 });

const EMPTY = {
  invoiceNumber: '',
  invoiceDate: new Date().toISOString().slice(0, 10),
  customerName: '', customerPhone: '', customerAddress: '',
  items: [newItem()],
  tax: 0, notes: '',
};

export default function InvoiceGenerator({ priest }) {
  const { priestFetch } = usePriestAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const previewRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await priestFetch('/api/priest/invoices');
      setList(data.invoices || []);
    } catch (e) { /* */ }
  }, [priestFetch]);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const updateItem = (i, k, v) => {
    setForm(p => {
      const items = [...p.items];
      items[i] = { ...items[i], [k]: v };
      return { ...p, items };
    });
  };
  const addItem = () => setForm(p => ({ ...p, items: [...p.items, newItem()] }));
  const removeItem = (i) => setForm(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const subtotal = form.items.reduce((s, it) =>
    s + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0);
  const total = subtotal + (Number(form.tax) || 0);

  const previewInvoice = (e) => {
    e.preventDefault();
    if (!form.customerName.trim()) return setErr('Customer name is required');
    if (form.items.length === 0)    return setErr('At least one item is required');
    setErr('');
    setPreview({ ...form, subtotal, total });
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const data = await priestFetch('/api/priest/invoices', {
        method: 'POST', body: JSON.stringify(form),
      });
      setPreview(p => ({ ...p, invoiceNumber: data.invoiceNumber }));
      setForm(EMPTY);
      await load();
      alert(`✅ Invoice saved: ${data.invoiceNumber}`);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const print = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Invoice ${preview.invoiceNumber || ''}</title>
      <style>${PRINT_CSS}</style></head><body>${previewRef.current.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 250);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await priestFetch(`/api/priest/invoices/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="ig-wrapper">
      <div className="mc-grid">
        <div className="mc-form-card">
          <h3>🧾 Create Invoice</h3>
          {err && <div className="auth-error">⚠️ {err}</div>}

          <form onSubmit={previewInvoice}>
            <div className="form-row-2">
              <div className="form-group">
                <label>Invoice # (auto if blank)</label>
                <input value={form.invoiceNumber}
                  onChange={e => update('invoiceNumber', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={form.invoiceDate}
                  onChange={e => update('invoiceDate', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label>Customer Name *</label>
              <input value={form.customerName}
                onChange={e => update('customerName', e.target.value)} />
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label>Phone</label>
                <input value={form.customerPhone}
                  onChange={e => update('customerPhone', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input value={form.customerAddress}
                  onChange={e => update('customerAddress', e.target.value)} />
              </div>
            </div>

            <div className="ig-items">
              <div className="ig-items-header">
                <strong>Items</strong>
                <button type="button" className="bm-btn bm-btn-edit" onClick={addItem}>+ Add Item</button>
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="ig-item-row">
                  <input className="ig-item-desc" placeholder="Description"
                    value={it.description}
                    onChange={e => updateItem(i, 'description', e.target.value)} />
                  <input type="number" min="0" step="0.01" placeholder="Qty"
                    value={it.quantity}
                    onChange={e => updateItem(i, 'quantity', e.target.value)} />
                  <input type="number" min="0" step="0.01" placeholder="Rate"
                    value={it.rate}
                    onChange={e => updateItem(i, 'rate', e.target.value)} />
                  <div className="ig-item-amt">
                    ₹{((Number(it.quantity) || 0) * (Number(it.rate) || 0)).toFixed(2)}
                  </div>
                  {form.items.length > 1 && (
                    <button type="button" className="ig-item-del"
                      onClick={() => removeItem(i)}>✕</button>
                  )}
                </div>
              ))}
            </div>

            <div className="ig-totals">
              <div><span>Subtotal:</span> <strong>₹{subtotal.toFixed(2)}</strong></div>
              <div>
                <label>Tax: </label>
                <input type="number" min="0" step="0.01" value={form.tax}
                  onChange={e => update('tax', e.target.value)}
                  style={{ width: 90 }} />
              </div>
              <div className="ig-grand"><span>Total:</span> <strong>₹{total.toFixed(2)}</strong></div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea rows="2" value={form.notes}
                onChange={e => update('notes', e.target.value)} />
            </div>

            <button type="submit" className="btn btn-primary">🔍 Preview Invoice</button>
          </form>
        </div>

        <div className="mc-list-card">
          <h3>📂 Saved Invoices</h3>
          {list.length === 0 ? (
            <p className="bm-empty-sm">No invoices yet.</p>
          ) : (
            <div className="mc-list">
              {list.map(inv => (
                <div key={inv.id} className="mc-item">
                  <div className="mc-item-head">
                    <strong>{inv.invoiceNumber}</strong>
                    <span className={`pd-status pd-status-${inv.status}`}>{inv.status}</span>
                  </div>
                  <div className="mc-item-meta">👤 {inv.customerName}</div>
                  <div className="mc-item-meta">📅 {inv.invoiceDate} • 💰 ₹{Number(inv.total).toFixed(2)}</div>
                  <div className="mc-item-actions">
                    <button className="bm-btn bm-btn-edit"
                      onClick={() => setPreview({ ...inv, items: inv.items || [] })}>👁️</button>
                    <button className="bm-btn bm-btn-del" onClick={() => remove(inv.id)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {preview && (
        <div className="pg-preview-overlay" onClick={() => setPreview(null)}>
          <div className="pg-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="pg-preview-actions">
              <button className="bm-btn bm-btn-edit" onClick={print}>🖨️ Print / PDF</button>
              {!preview.id && (
                <button className="bm-btn bm-btn-done" onClick={save} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save'}
                </button>
              )}
              <button className="bm-btn bm-btn-del" onClick={() => setPreview(null)}>✕</button>
            </div>

            <div ref={previewRef} className="ig-invoice">
              <div className="ig-invoice-header">
                <div>
                  <h1>🕉️ INVOICE</h1>
                  <p><strong>{priest?.name}</strong></p>
                  <p>{priest?.phone} • {priest?.email}</p>
                </div>
                <div className="ig-invoice-meta">
                  <p><strong>#</strong> {preview.invoiceNumber || 'AUTO'}</p>
                  <p><strong>Date:</strong> {preview.invoiceDate}</p>
                </div>
              </div>

              <div className="ig-invoice-to">
                <strong>Bill To:</strong>
                <p>{preview.customerName}</p>
                <p>{preview.customerPhone}</p>
                <p>{preview.customerAddress}</p>
              </div>

              <table className="ig-invoice-table">
                <thead>
                  <tr><th>#</th><th>Description</th><th>Qty</th><th>Rate</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  {preview.items.map((it, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{it.description}</td>
                      <td>{it.quantity}</td>
                      <td>₹{Number(it.rate).toFixed(2)}</td>
                      <td>₹{((Number(it.quantity)||0) * (Number(it.rate)||0)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan="4" align="right">Subtotal</td>
                      <td>₹{Number(preview.subtotal).toFixed(2)}</td></tr>
                  <tr><td colSpan="4" align="right">Tax</td>
                      <td>₹{Number(preview.tax || 0).toFixed(2)}</td></tr>
                  <tr className="ig-total-row">
                    <td colSpan="4" align="right"><strong>TOTAL</strong></td>
                    <td><strong>₹{Number(preview.total).toFixed(2)}</strong></td>
                  </tr>
                </tfoot>
              </table>

              {preview.notes && (
                <div className="ig-invoice-notes"><strong>Notes:</strong> {preview.notes}</div>
              )}

              <div className="ig-invoice-footer">
                <p>🙏 Thank you for your patronage 🙏</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PRINT_CSS = `
  body { font-family: 'Segoe UI', sans-serif; padding: 30px; color: #222; }
  .ig-invoice-header { display: flex; justify-content: space-between; border-bottom: 3px solid #d4a017; padding-bottom: 15px; }
  .ig-invoice-header h1 { color: #c2185b; margin: 0; }
  .ig-invoice-to { margin: 20px 0; }
  .ig-invoice-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
  .ig-invoice-table th, .ig-invoice-table td { padding: 8px; border-bottom: 1px solid #ddd; text-align: left; }
  .ig-invoice-table th { background: #fff5e6; color: #800020; }
  .ig-total-row { background: #fff5e6; font-size: 1.1em; }
  .ig-invoice-footer { text-align: center; margin-top: 30px; color: #c2185b; }
`;
